import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../../../client';
import BoxGradientBorder from '../../../../components/BoxGradientBorder';
import Formatter from '../../../../i18n/formatter';
import { __, getCurrency } from '../../../../i18n/l10n';
import theme from '../../../../styled-components/dark';
import { withOpacity } from '../../../../styled-components/dark/helpers';
import palette from '../../../../styled-components/palette';
import { PromotionStatuses, type PromotionStatus } from '../../../../types/promotions';
import { DefaultMissionBackground } from '../../assets/img';
import { MISSIONS_STATE_TRANSITION_DURATION } from '../../constants/transitions';
import { MissionCardComplexeStatuses, type MissionCardComplexeStatus } from '../../types/components';
import { MissionOperationTypes, type MissionOperationType } from '../../types/state/missions';
import MissionBoxWrapper from '../common/MissionBoxWrapper';
import MissionRequirementTag from '../common/MissionRequirementTag';
import MissionCardCTA from '../MissionCardCTA/MissionCardCTA';
import MissionFreebetsPotentialGainContainer from '../MissionFreebetsPotentialGain/MissionFreebetsPotentialGainContainer';
import MissionTimerContainer from '../MissionTimer/MissionTimerContainer';

const CARD_BORDER_RADIUS = 1; // em

const emojiList = ['💪', '💸', '🤑'];

const CardWrapper = styled.div``;

const Wrapper = styled(BoxGradientBorder)`
  position: relative;
  color: white;
  padding: 0px;

  cursor: pointer;
  border-radius: ${CARD_BORDER_RADIUS}em;
  --BoxGradientBorder-width: 1px;
  --BoxGradientBorder-gradient: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.1) -4%);
  --BoxGradientBorder-fallbackColor: rgba(255, 255, 255, 0.1);
  --BoxGradientBorder-afterZIndex: 1;
`;

const OverlayManager = styled.div`
  position: relative;
  transform-style: preserve-3d;
  border-radius: ${CARD_BORDER_RADIUS}em;
`;

type CardOverlayType = {
  $missionStatus: MissionCardComplexeStatus;
};

const CardOverlay = styled.div<CardOverlayType>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;

  border-radius: ${CARD_BORDER_RADIUS}em;
  background: #00000077;
  transform: translateZ(100px);
  pointer-events: none;

  ${({ $missionStatus }) => {
    switch ($missionStatus) {
      case MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS:
      case MissionCardComplexeStatuses.EXPIRED:
        return css`
          opacity: 1;
        `;
      default:
        return css`
          opacity: 0;
        `;
    }
  }}
`;

const WonCardOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;

  border-radius: ${CARD_BORDER_RADIUS}em;
  transform: translateZ(2px);
  pointer-events: none;

  opacity: 1;
  background: linear-gradient(
    to bottom,
    ${withOpacity(palette.greenwinamax650, 'ee')},
    ${withOpacity(palette.greenwinamax650, 'aa')} 40%,
    rgba(255, 255, 255, 0) 100%
  );
`;

const ContentWrapper = styled.div`
  position: relative;
  border-radius: inherit;
  transform-style: preserve-3d;
`;

type ImageBackgroundType = {
  $imageURL?: string | null;
};

const ImageBackground = styled.div<ImageBackgroundType>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${CARD_BORDER_RADIUS}em;

  transform: translateZ(0px);

  ${({ $imageURL }) => css`
    background-image: url(${$imageURL ?? DefaultMissionBackground});
    background-position: start center;
    background-repeat: no-repeat;
    background-size: 100%, auto;
  `};
`;

const LinearGradientOpacityTransition = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;

  height: calc(100% + 1em);
  border-radius: 0 0 ${CARD_BORDER_RADIUS}em ${CARD_BORDER_RADIUS}em;
  transform: translateZ(1px);

  background-image: linear-gradient(
    to bottom,
    transparent,
    ${withOpacity(theme.MissionCard.bottomRowBackground, 'aa')} 20%,
    ${withOpacity(theme.MissionCard.bottomRowBackground, 'ff')} 55%
  );
`;

const BlurMask = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 0 0 ${CARD_BORDER_RADIUS}em ${CARD_BORDER_RADIUS}em;
  transform: translateZ(1px);

  --BoostedOddCardContentContentWrapper-mask: linear-gradient(to bottom, transparent, #000000cc 20%, #000000 55%);

  -webkit-backdrop-filter: blur(0.2em);
  backdrop-filter: blur(0.2em);

  mask: var(--BoostedOddCardContentContentWrapper-mask);
  -webkit-mask: var(--BoostedOddCardContentContentWrapper-mask);
`;

const Content = styled.div`
  position: relative;
  width: 100%;
  min-height: ${Target.__MOBILE__ ? '8em' : '10.5em'};
  top: 0;
  left: 0;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  transform-style: preserve-3d;
`;

type TopRowType = {
  $displayShadow: boolean;
};

const TopRow = styled.div<TopRowType>`
  width: 100%;

  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${CARD_BORDER_RADIUS}em ${CARD_BORDER_RADIUS}em 0 0;
  padding: 1em 1em 0.67em;
  transform: translateZ(5px);

  ${({ $displayShadow }) =>
    $displayShadow
      ? css`
          background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 55%, rgba(0, 0, 0, 0));
        `
      : undefined}
`;

const TopRowItem = styled.div`
  flex: 1;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  gap: 0.66em;
`;

const EmojiWrapper = styled.div`
  font-size: 120%;
`;

const StyledMissionTimerContainer = styled(MissionTimerContainer)`
  transform: translateZ(0);
  flex: 1;
`;

type MissionFreebetsPotentialGainWrapperType = {
  $isWon: boolean;
};

const MissionFreebetsPotentialGainWrapper = styled(MissionBoxWrapper)<MissionFreebetsPotentialGainWrapperType>`
  overflow: visible;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.1em 0.6em;
  border-radius: 0.5em;

  ${({ $isWon }) =>
    $isWon
      ? css`
          background-image: linear-gradient(
            to top,
            ${withOpacity(palette.greenwinamax500, '54')},
            ${withOpacity(palette.greenwinamax500, '22')}
          );
        `
      : undefined}
`;

const StyledMissionFreebetsPotentialGainContainer = styled(MissionFreebetsPotentialGainContainer)`
  font-size: 120%;
`;

const Logo = styled.img`
  object-fit: contain;
  width: 100%;
  max-height: ${Target.__MOBILE__ ? 5.2 : 6}em;
  align-self: center;
  transform: translateZ(1px);
  margin-top: ${Target.__MOBILE__ ? 0 : 0.75}em;
  margin-bottom: ${Target.__MOBILE__ ? 1.33 : 2.25}em;
`;

const EmptyLogo = styled.div`
  object-fit: contain;
  width: ${Target.__MOBILE__ ? 5.2 : 6}em;
  height: ${Target.__MOBILE__ ? 5.2 : 6}em;
  margin-top: ${Target.__MOBILE__ ? 0 : 0.75}em;
  margin-bottom: ${Target.__MOBILE__ ? 1.33 : 2.25}em;
`;

const BottomRow = styled.div`
  position: relative;
  width: 100%;
  padding: 0 1em 1em;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transform-style: preserve-3d;
`;

const Description = styled.div`
  color: white;
  width: 100%;
  text-align: left;
  font-weight: 500;
  letter-spacing: 0.03em;
  transform: translateZ(2px);

  &:first-letter {
    text-transform: capitalize;
  }

  font-size: 105%;
`;

type ButtonRowType = {
  $alignRight: boolean;
};

const ButtonRow = styled.div<ButtonRowType>`
  display: flex;
  margin-top: 0.67em;

  transform: translateZ(2px);
  justify-content: ${({ $alignRight }) => ($alignRight ? 'flex-end' : 'space-between')};

  width: 100%;
`;

type RequirementsRowType = {
  $shouldTransition: boolean;
};

const RequirementsRow = styled.div<RequirementsRowType>`
  display: flex;

  > *:not(:last-child) {
    margin-right: 0.33em;
  }

  > * {
    flex: 1;
  }

  opacity: 1;

  ${({ $shouldTransition }) => {
    if ($shouldTransition) {
      return css`
        transition: opacity ${MISSIONS_STATE_TRANSITION_DURATION}ms ease-in-out;
      `;
    }
  }}
`;

export type MissionCardProps = {
  className?: string;
  missionId: number;
  description?: string;
  logoURL?: string | null;
  backgroundURL?: string | null;
  achvMinBetOdds?: number;
  achvMinPlacedAmount?: number;
  missionStatus: MissionCardComplexeStatus;
  operationType?: MissionOperationType;
  onClick?: () => void;
  isUserConnected: boolean;
  optInRequired: boolean;
  hasUserRegistered: boolean;
  showTimer: boolean;
  betRedirectionOnClick: () => void;
  shouldTransition: boolean;
  promotionStatus: PromotionStatus;
  missionsModerator: boolean;
  hasPotentialAmount: boolean;
};

const MissionCard = ({
  className = '',
  missionId,
  description,
  logoURL,
  backgroundURL,
  achvMinBetOdds,
  achvMinPlacedAmount,
  missionStatus,
  operationType,
  onClick,
  isUserConnected,
  optInRequired,
  hasUserRegistered,
  showTimer,
  betRedirectionOnClick,
  shouldTransition,
  promotionStatus,
  missionsModerator,
  hasPotentialAmount,
}: MissionCardProps) => {
  const hasNoBottomRow = useMemo(
    () => operationType === MissionOperationTypes.OTHER && !optInRequired,
    [operationType, optInRequired],
  );
  const isUserEnrollable = useMemo(
    () => !missionsModerator && promotionStatus === PromotionStatuses.DEFAULT,
    [missionsModerator, promotionStatus],
  );

  const onCardClick = useCallback(() => {
    if (hasNoBottomRow) {
      betRedirectionOnClick();
    } else {
      onClick?.();
    }
  }, [betRedirectionOnClick, onClick, hasNoBottomRow]);

  const missionIsExpired = useMemo(
    () =>
      missionStatus === MissionCardComplexeStatuses.EXPIRED ||
      missionStatus === MissionCardComplexeStatuses.EXPIRED_BUT_PENDING_BETS,
    [missionStatus],
  );

  const displayTimer = useMemo(() => missionIsExpired || showTimer, [missionIsExpired, showTimer]);

  const isWon = useMemo(() => missionStatus === MissionCardComplexeStatuses.WON, [missionStatus]);

  return (
    <CardWrapper className={className}>
      <Wrapper>
        <OverlayManager onClick={onCardClick}>
          <CardOverlay $missionStatus={missionStatus} />
          {isWon ? <WonCardOverlay /> : null}
          <ContentWrapper>
            <Content>
              <ImageBackground $imageURL={backgroundURL} />
              <TopRow $displayShadow={displayTimer || hasPotentialAmount}>
                {displayTimer ? <StyledMissionTimerContainer missionId={missionId} /> : null}
                {hasPotentialAmount ? (
                  <TopRowItem>
                    {isWon ? <EmojiWrapper>{emojiList[missionId % 3]}</EmojiWrapper> : null}
                    <MissionFreebetsPotentialGainWrapper $isWon={isWon}>
                      <StyledMissionFreebetsPotentialGainContainer missionId={missionId} smallIcon />
                    </MissionFreebetsPotentialGainWrapper>
                  </TopRowItem>
                ) : null}
              </TopRow>
              {logoURL ? <Logo src={logoURL} /> : <EmptyLogo />}
              {!hasNoBottomRow ? (
                <BottomRow>
                  <LinearGradientOpacityTransition />
                  <BlurMask />
                  {description ? <Description>{description}</Description> : null}
                  <ButtonRow $alignRight={!achvMinBetOdds && !achvMinPlacedAmount}>
                    {achvMinBetOdds || achvMinPlacedAmount ? (
                      <RequirementsRow $shouldTransition={shouldTransition}>
                        {achvMinBetOdds ? (
                          <MissionRequirementTag label={__('Min. odds:')} value={Formatter.formatOdd(achvMinBetOdds)} />
                        ) : null}
                        {achvMinPlacedAmount ? (
                          <MissionRequirementTag
                            label={__('Min. wager:')}
                            value={Formatter.formatBankroll(
                              achvMinPlacedAmount,
                              getCurrency(),
                              !(((achvMinPlacedAmount ?? 0) / 100) % 1),
                            )}
                          />
                        ) : null}
                      </RequirementsRow>
                    ) : null}
                    {isUserEnrollable ? (
                      <MissionCardCTA
                        missionId={missionId}
                        missionStatus={missionStatus}
                        operationType={operationType}
                        isUserConnected={isUserConnected}
                        optInRequired={optInRequired}
                        hasUserRegistered={hasUserRegistered}
                        shouldTransition={shouldTransition}
                      />
                    ) : null}
                  </ButtonRow>
                </BottomRow>
              ) : null}
            </Content>
          </ContentWrapper>
        </OverlayManager>
      </Wrapper>
    </CardWrapper>
  );
};

export default MissionCard;
